/**
 * The form-side contract of the "Make this recurring" block — everything a
 * host form imports OTHER than the component itself (kept out of
 * `RecurringSection.tsx` so that file exports only components and React
 * fast-refresh keeps working; precedent: `expenseLineItemSchema.ts`).
 *
 *  - {@link recurringFormFields} — the two zod fields to spread into the
 *    form's base `z.object` BEFORE its trailing `.refine()`s.
 *  - {@link recurringFormRefinement} — the `.superRefine()` a zod-resolver
 *    form chains LAST; {@link validateRecurrence} — the same rules for the
 *    resolver-less journal modal, called in `onSubmit`.
 *  - {@link resolveRecurringDefaults} — the recurring half of `defaultValues`
 *    for the three ways a form opens (off / `defaultRecurring` / template-edit).
 */
import { type FieldPath, type UseFormSetError } from 'react-hook-form';
import { z } from 'zod';
import type { RecurringDocumentType, RecurringTemplateBaseWire } from '@releve/shared';
import {
  defaultRecurrenceValues,
  fromTemplate,
  recurrenceFormSchema,
  recurrenceFormSchemaFor,
  type RecurrenceFormSchemaContext,
  type RecurrenceFormValues,
} from '@/lib/recurrence';

/** The feature flag every recurring surface gates on. */
export const RECURRING_DOCUMENTS_FEATURE = 'recurring_documents';

// ---------------------------------------------------------------------------
// The form-side contract
// ---------------------------------------------------------------------------

/** The two fields each host form adds to its schema / `defaultValues`. */
export interface RecurringFormFields {
  /** Form-local; NEVER transmitted. Reveals the schedule; unticking clears `recurrence`. */
  recurring_enabled: boolean;
  recurrence?: RecurrenceFormValues;
}

/**
 * Spread into the form's base `z.object({ … })` BEFORE its trailing
 * `.refine()`s. Structural validation of the schedule rides in `recurrence`;
 * the context-dependent rules come from {@link recurringFormRefinement}.
 */
export const recurringFormFields = {
  recurring_enabled: z.boolean().default(false),
  recurrence: recurrenceFormSchema.optional(),
};

/** `recurring_enabled: false`, nothing else — the create-mode default. */
export const RECURRING_OFF: RecurringFormFields = { recurring_enabled: false, recurrence: undefined };

/**
 * Every schedule issue for the form's current values — the structural rules
 * plus the context-dependent ones (an unchanged template start is never
 * re-checked against today, a moved one must be `>= org-today` or is refused
 * outright once the schedule has run, AP/AR need `due_days`). Empty when the
 * box is off. Paths are prefixed `recurrence.…`. Shared by {@link recurringFormRefinement} (zod-resolver forms) and
 * {@link validateRecurrence} (the resolver-less journal modal).
 */
function collectRecurrenceIssues(
  values: RecurringFormFields,
  context: RecurrenceFormSchemaContext,
): Array<{ path: (string | number)[]; message: string }> {
  if (!values.recurring_enabled) return [];
  if (!values.recurrence) {
    return [{ path: ['recurrence', 'name'], message: 'recurring.form.errors.nameRequired' }];
  }
  const result = recurrenceFormSchemaFor(context).safeParse(values.recurrence);
  if (result.success) return [];
  return result.error.issues.map((issue) => ({ path: ['recurrence', ...issue.path], message: issue.message }));
}

/**
 * The `.superRefine()` a zod-resolver form chains LAST. `getContext` is read
 * at validation time (pass a ref-reader), so the schema object itself stays
 * stable across renders while org-today / the template mode can change.
 */
export function recurringFormRefinement(getContext: () => RecurrenceFormSchemaContext) {
  return (values: RecurringFormFields, ctx: z.RefinementCtx): void => {
    for (const issue of collectRecurrenceIssues(values, getContext())) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: issue.path, message: issue.message });
    }
  };
}

/**
 * Resolver-less forms (the journal modal) call this in `onSubmit`: it runs the
 * FULL schedule schema (structural + context) and writes every issue onto the
 * form as an inline error. Returns `true` when the submit may proceed.
 */
export function validateRecurrence<TFieldValues extends RecurringFormFields>(
  values: TFieldValues,
  context: RecurrenceFormSchemaContext,
  setError: UseFormSetError<TFieldValues>,
): boolean {
  // `recurrenceFormSchemaFor` = the structural schema + the context rules, so
  // one pass yields every issue; keep the first per path like the resolver.
  const issues = collectRecurrenceIssues(values, context);
  if (issues.length === 0) return true;
  const seen = new Set<string>();
  for (const issue of issues) {
    const name = issue.path.join('.');
    if (seen.has(name)) continue;
    seen.add(name);
    setError(name as FieldPath<TFieldValues>, { type: 'manual', message: issue.message });
  }
  return false;
}

/**
 * `defaultValues` for a form that opens ALREADY recurring — the "New
 * recurring…" menu (`defaultRecurring`) or template-edit mode. Fed through
 * `defaultValues` / `reset(values)`, never `setValue`, so `isDirty` stays
 * false until the user acts (`docs/FULLSCREEN_FORM_MODALS.md`).
 */
export function recurringDefaultsForNew(options: {
  documentType: RecurringDocumentType;
  orgToday: string;
  documentDate?: string | null;
  dueDays?: number | null;
}): RecurringFormFields {
  return {
    recurring_enabled: true,
    recurrence: defaultRecurrenceValues({
      orgToday: options.orgToday,
      documentDate: options.documentDate,
      documentType: options.documentType,
      dueDays: options.dueDays,
    }),
  };
}

/** Template-edit mode: the saved rule as form values (box ticked). */
export function recurringDefaultsFromTemplate(template: RecurringTemplateBaseWire): RecurringFormFields {
  return { recurring_enabled: true, recurrence: fromTemplate(template) };
}

/**
 * The three ways a form opens, resolved to its recurring `defaultValues`:
 * template-edit → the template's rule; `defaultRecurring` → a fresh monthly
 * schedule from org-today; otherwise off.
 */
export function resolveRecurringDefaults(options: {
  documentType: RecurringDocumentType;
  orgToday: string;
  template?: RecurringTemplateBaseWire | null;
  defaultRecurring?: boolean;
  documentDate?: string | null;
  dueDays?: number | null;
}): RecurringFormFields {
  if (options.template) return recurringDefaultsFromTemplate(options.template);
  if (options.defaultRecurring) return recurringDefaultsForNew(options);
  return RECURRING_OFF;
}

